import { lastInboundDatable } from './dashboard';

// ── Conversation recency (Engage list order) ──────────────────────────────
// Human-only by construction: both sources exclude ai_agent / crisis_alert / system,
// so automated traffic never moves a conversation.
// Spec: docs/specs/2026-07-30-engage-conversation-ordering.md

function toMs(v) {
  if (v == null || v === '') return null;
  const ms = typeof v === 'number' ? v * 1000 : new Date(v).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** Epoch ms of the last human message either way, or null if never. */
export function lastInteractionAt(patient, inboundDates) {
  const staff = toMs(patient?.lastHumanOutreachAt);
  const inbound = toMs(inboundDates?.get?.(String(patient?.userid)));
  if (staff === null) return inbound;
  if (inbound === null) return staff;
  return Math.max(staff, inbound);
}

// The care team wrote after the patient's last message, so the row's preview quotes an answered one. Never claimed on
// an unread row or one the cache cannot date (no time, or a clinic-line message): it can lag or never hold the preview.
export function careTeamSpokeLast(patient, inboundDates) {
  if (patient?.unread?.has_unread || !lastInboundDatable(patient?.unread)) return false;
  const staff = toMs(patient?.lastHumanOutreachAt);
  const inbound = toMs(inboundDates?.get?.(String(patient?.userid)));
  return staff !== null && inbound !== null && staff > inbound;
}

// Unread keys as "now" when its time is unknown or not the message's own (the WF26 cache never holds a
// clinic-line message): over-surface rather than bury. `held` = the open chat, kept in place once its dot clears.
export function interactionSortKey(patient, inboundDates, now = Date.now(), held = false) {
  const unread = held || !!patient?.unread?.has_unread;
  if (unread && !lastInboundDatable(patient?.unread)) return now;
  const at = lastInteractionAt(patient, inboundDates);
  if (at !== null) return at;
  return unread ? now : null;
}

// The row's time label: blank when the last message is one the cache cannot date.
export function conversationTimeMs(patient, inboundDates) {
  return lastInboundDatable(patient?.unread) ? lastInteractionAt(patient, inboundDates) : null;
}

/**
 * Most recent conversation first; never-messaged last, keeping server order.
 * @param {string | number | null} [heldId=null]
 */
export function byLastInteractionDesc(inboundDates, now = Date.now(), heldId = null) {
  const key = (p) => interactionSortKey(p, inboundDates, now, heldId != null && String(p?.userid) === String(heldId));
  return (a, b) => {
    const ka = key(a);
    const kb = key(b);
    if (ka === kb) return 0;
    if (ka === null) return 1;
    if (kb === null) return -1;
    return kb - ka;
  };
}

// Messages-style stamp: the time today, "Yesterday", a weekday within the week, then a date.
// Days are counted on the calendar in `timeZone` (default: the browser's), not in 24 h blocks.
// The list header's count line; '' until there is something true to say.
export function engageListSubtitle({ loaded, error, rows, total, hasMore, filtered, serverUnread }) {
  if (!loaded || error) return '';
  const fmt = (n) => n.toLocaleString('en-US');
  if (rows.length < total) return `Showing ${fmt(rows.length)} of ${fmt(total)}`;
  if (filtered) return `${fmt(total)} matching`;
  // Whole roster loaded: count the rows, which carry the optimistic read; the server count lags it.
  const unread = hasMore ? serverUnread : rows.filter((r) => r.unread?.has_unread).length;
  const patients = `${fmt(total)} patient${total === 1 ? '' : 's'}`;
  return unread ? `${patients} · ${unread} unread` : patients;
}

const STAMP_FORMATS = {
  day: ['en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }],
  time: ['en-US', { hour: 'numeric', minute: '2-digit' }],
  weekday: ['en-US', { weekday: 'short' }],
  date: ['en-US', { month: 'short', day: 'numeric' }],
  dateYear: ['en-US', { month: 'short', day: 'numeric', year: 'numeric' }],
};
const stampFormatters = new Map();
// Built once per zone: the list formats ~200 stamps on every clock tick.
function stampFormat(kind, timeZone) {
  const key = `${kind}|${timeZone ?? ''}`;
  if (!stampFormatters.has(key)) {
    const [locale, opts] = STAMP_FORMATS[kind];
    stampFormatters.set(key, new Intl.DateTimeFormat(locale, timeZone ? { ...opts, timeZone } : opts));
  }
  return stampFormatters.get(key);
}

export function conversationTimeLabel(ms, nowMs, timeZone) {
  if (ms == null || !Number.isFinite(ms) || !Number.isFinite(nowMs)) return '';
  const day = stampFormat('day', timeZone);
  const msDay = day.format(new Date(ms));
  const nowDay = day.format(new Date(nowMs));
  const days = Math.round((Date.parse(nowDay) - Date.parse(msDay)) / 86400000);
  const fmt = (kind) => stampFormat(kind, timeZone).format(new Date(ms));
  if (days <= 0) return fmt('time');
  if (days === 1) return 'Yesterday';
  if (days < 7) return fmt('weekday');
  return fmt(msDay.slice(0, 4) === nowDay.slice(0, 4) ? 'date' : 'dateYear');
}

/**
 * Pre-generated "AI" message templates for the POC.
 * In production these would come from an LLM.
 */
/**
 * Context-aware smart message suggestions for the Engage workspace.
 * Returns 3 templates based on the patient's engagement bucket.
 */
export function getSmartMessages(patient, engageReason) {
  const name = patient?.firstname || 'there';
  const daysSince = patient?.lastEpoch
    ? Math.floor((Date.now() / 1000 - patient.lastEpoch) / 86400)
    : null;
  const bpStr = patient?.lastSystolic ? `${patient.lastSystolic} mmHg` : null;

  const templates = {
    'at-risk': [
      { id: 'measure-reminder', label: '📋 Measurement reminder', text: `Hi ${name}, your care team noticed we haven't received a blood pressure reading recently. A quick measurement today helps us stay on track with your care plan and ensure timely reporting. Could you take a reading when you have a moment?` },
      { id: 'submission-deadline', label: '⏰ Submission deadline', text: `Hi ${name}, we have an upcoming reporting window and want to make sure your latest readings are captured. Taking a blood pressure measurement in the next few days would be very helpful. Let us know if you need any assistance with your device.` },
      { id: 'care-checkin', label: '👋 Care team check-in', text: `Hi ${name}, this is your ACCESS care team checking in. We want to make sure everything is going well with your monitoring. How are you feeling? If you have any questions about your care plan or device, we're here to help.` },
    ],
    'disengaging': [
      { id: 'reengage', label: '🔄 Re-engagement', text: `Hi ${name}, we've noticed your readings have become less frequent${daysSince ? ` — it's been ${daysSince} days since your last one` : ''}. Even a quick daily check makes a real difference in managing your health. We're here to support you every step of the way.` },
      { id: 'program-value', label: '💡 Program value', text: `Hi ${name}, did you know that patients who monitor regularly see better blood pressure outcomes within just a few months? Your ACCESS program is designed to help you reach your targets${bpStr ? ` (last reading: ${bpStr})` : ''}. Let's get back on track together.` },
      { id: 'churn-question', label: '❓ Churn question', text: `Hi ${name}, we've noticed a change in your monitoring pattern and want to understand how the program is working for you. Is there anything making it difficult to take regular readings? Your feedback helps us improve your experience.` },
    ],
    'no-recent': [
      { id: 'device-help', label: '🔧 Device help', text: `Hi ${name}, we noticed it's been${daysSince ? ` ${daysSince} days` : ' a while'} since your last reading. Sometimes devices need a quick setup refresh. Are you having any trouble with your blood pressure monitor? We can walk you through it.` },
      { id: 'gentle-reminder', label: '📱 Gentle reminder', text: `Hi ${name}, just a friendly reminder to take your blood pressure reading today. Regular monitoring helps your care team track your progress and adjust your plan if needed. It only takes a minute!` },
      { id: 'schedule-suggestion', label: '📅 Schedule suggestion', text: `Hi ${name}, many patients find it helpful to take their reading at the same time each day — like right after morning coffee or before bed. Would you like help setting up a daily reminder? Consistency is key to getting the most out of your care plan.` },
    ],
  };

  const defaults = [
    { id: 'general-checkin', label: '👋 General check-in', text: `Hi ${name}, this is your ACCESS care team. We wanted to check in and see how things are going${bpStr ? ` — your last reading was ${bpStr}` : ''}. Is there anything we can help you with today?` },
    { id: 'wellness-tip', label: '💊 Wellness tip', text: `Hi ${name}, here's a quick tip: reducing sodium intake by even a small amount can help lower blood pressure over time. Try seasoning with herbs and spices instead of salt. Every small change adds up!` },
    { id: 'feedback-request', label: '📝 Feedback request', text: `Hi ${name}, we hope the ACCESS program has been helpful for managing your blood pressure. We're always looking to improve — could you share what's working well and what could be better? Your input makes a difference.` },
  ];

  return templates[engageReason] || defaults;
}

// Keyboard walk down (+1) or up (-1) the conversation list. No wrap: the ends are
// where the operator stops, not where they jump to the other end of the roster.
// With nothing selected, down starts at the top and up at the bottom.
export function stepSelection(ids, currentId, delta) {
  if (!Array.isArray(ids) || ids.length === 0) return null;
  const at = ids.findIndex((id) => String(id) === String(currentId));
  if (at === -1) return delta > 0 ? ids[0] : ids[ids.length - 1];
  const next = at + delta;
  if (next < 0 || next >= ids.length) return null;
  return ids[next];
}
